import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, Book, BookOpen, 
  CheckCircle, Type, AlignLeft, Volume2, 
  Star, ChevronDown, ArrowLeft, ArrowRight,
  AlertTriangle
} from 'lucide-react';
import api from '../utils/api';
import { useWordPopover } from '../shared/reader/useWordPopover';
import SharedWordPopover from '../shared/reader/WordPopover';
import GrammarWidget from '../components/GrammarWidget';
import GrammarOverlay from '../components/GrammarOverlay';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useI18n } from '../contexts/I18nContext';

const BibleReader = () => {
  const { courseId, bookId, chapter } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const chapterNum = parseInt(chapter, 10);
  
  const [chapterData, setChapterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('text'); // 'text' or 'sentence'
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [clickedWords, setClickedWords] = useState(new Set());
  const [completingLesson, setCompletingLesson] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  
  // Pagination state for text mode
  const [currentPage, setCurrentPage] = useState(0);
  const [versesPerPage, setVersesPerPage] = useState(8); // Default, will be calculated dynamically
  
  // Font size state for sentence mode (16-32, default 24)
  const [sentenceFontSize, setSentenceFontSize] = useState(() => {
    const saved = localStorage.getItem('bible_sentence_font_size');
    return saved ? parseInt(saved, 10) : 24;
  });
  
  // Word popover state — shared popover hook
  // `language` is destructured from chapterData below; use courseId for initial default.
  const bibleLangCode = courseId === 'biblical_hebrew' ? 'hbo' : 'grc';
  const popover = useWordPopover({ languageCode: bibleLangCode });

  // Grammar state
  const [selectedGrammar, setSelectedGrammar] = useState(null);
  const [verseGrammarPoints, setVerseGrammarPoints] = useState([]);
  const [grammarLoading, setGrammarLoading] = useState(false);

  // Extract grammar points from current verse tokens
  const extractGrammarFromTokens = useCallback(async (tokens) => {
    if (!tokens || tokens.length === 0) {
      setVerseGrammarPoints([]);
      return;
    }
    
    const grammarSet = new Map(); // Use Map to dedupe by grammar tag
    
    for (const token of tokens) {
      if (!token.is_word) continue;
      
      // Get grammar/morphology from token
      const morph = token.morph || token.grammar || token.morphology;
      if (morph) {
        if (!grammarSet.has(morph)) {
          grammarSet.set(morph, {
            id: morph,
            tag: morph,
            word_example: token.text,
            lemma: token.lemma,
            type: 'morphology'
          });
        }
      }
    }
    
    const points = Array.from(grammarSet.values());
    
    // Try to fetch explanations for top grammar points
    if (points.length > 0) {
      setGrammarLoading(true);
      try {
        const language = courseId === 'biblical_hebrew' ? 'hebrew' : 'greek';
        const res = await api.get(`/bible/grammar-list/${language}`);
        const knownTopics = res.data.topics_by_type || {};
        
        // Match grammar points with known explanations
        const enrichedPoints = points.map(point => {
          for (const [type, topics] of Object.entries(knownTopics)) {
            for (const topic of topics) {
              if (point.tag.toLowerCase().includes(topic.id) || 
                  topic.id.includes(point.tag.toLowerCase())) {
                return {
                  ...point,
                  name: topic.name,
                  hebrew_name: topic.hebrew_name,
                  greek_name: topic.greek_name,
                  type: type
                };
              }
            }
          }
          return point;
        });
        
        setVerseGrammarPoints(enrichedPoints);
      } catch (err) {
        console.error('Failed to enrich grammar:', err);
        setVerseGrammarPoints(points);
      } finally {
        setGrammarLoading(false);
      }
    } else {
      setVerseGrammarPoints([]);
    }
  }, [courseId]);

  // Audio state for sentence mode
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const audioRef = useRef(null);
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const containerRef = useRef(null);
  const contentRef = useRef(null);

  // Calculate verses per page based on viewport height and track mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      
      if (!contentRef.current) return;
      const viewportHeight = window.innerHeight;
      const headerHeight = 200; // Approximate header + navigation height
      const availableHeight = viewportHeight - headerHeight;
      // Estimate ~80px per verse on average (depends on content)
      const estimated = Math.max(3, Math.floor(availableHeight / 80));
      setVersesPerPage(estimated);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Save font size preference
  useEffect(() => {
    localStorage.setItem('bible_sentence_font_size', sentenceFontSize.toString());
  }, [sentenceFontSize]);

  useEffect(() => {
    fetchChapterContent();
    setCurrentSentenceIndex(0);
    setCurrentPage(0);
    setClickedWords(new Set());
  }, [courseId, bookId, chapterNum]);

  const fetchChapterContent = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/bible/chapter/${courseId}/${bookId}/${chapterNum}?mode=${mode}`);
      setChapterData(response.data);
    } catch (err) {
      setError(t('bibleReader.loadFailed'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleWordClick = useCallback((token, event) => {
    if (!token.is_word) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    // Track clicked word
    setClickedWords(prev => new Set([...prev, token.normalized_form]));
    
    // Open shared popover
    popover.open(token, event);
  }, [popover]);

  // Extract grammar from current verse when in sentence mode
  useEffect(() => {
    if (mode === 'sentence' && chapterData?.verses?.[currentSentenceIndex]) {
      const currentVerse = chapterData.verses[currentSentenceIndex];
      extractGrammarFromTokens(currentVerse.tokens || []);
    }
  }, [mode, currentSentenceIndex, chapterData, extractGrammarFromTokens]);

  const navigateChapter = (direction) => {
    if (!chapterData) return;
    
    const target = direction === 'next' ? chapterData.next_chapter : chapterData.prev_chapter;
    if (target) {
      navigate(`/bible/${courseId}/${target.book_id}/${target.chapter}`);
    }
  };

  const navigateSentence = (direction) => {
    if (!chapterData?.verses) return;
    
    if (direction === 'next' && currentSentenceIndex < chapterData.verses.length - 1) {
      setCurrentSentenceIndex(prev => prev + 1);
    } else if (direction === 'prev' && currentSentenceIndex > 0) {
      setCurrentSentenceIndex(prev => prev - 1);
    }
  };

  // Pagination helpers for text mode
  const totalPages = chapterData?.verses ? Math.ceil(chapterData.verses.length / versesPerPage) : 0;
  const paginatedVerses = chapterData?.verses?.slice(
    currentPage * versesPerPage,
    (currentPage + 1) * versesPerPage
  ) || [];
  const isLastPage = currentPage >= totalPages - 1;

  const navigatePage = (direction) => {
    if (direction === 'next' && currentPage < totalPages - 1) {
      setCurrentPage(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (direction === 'prev' && currentPage > 0) {
      setCurrentPage(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Font size controls for sentence mode
  const increaseFontSize = () => {
    setSentenceFontSize(prev => Math.min(32, prev + 2));
  };
  
  const decreaseFontSize = () => {
    setSentenceFontSize(prev => Math.max(16, prev - 2));
  };

  // Get TTS language code - map Biblical languages to modern equivalents
  const getTTSLanguageCode = () => {
    if (courseId === 'biblical_hebrew') return 'he'; // Modern Hebrew for Biblical Hebrew
    if (courseId === 'biblical_greek' || courseId === 'greek_nt') return 'el'; // Modern Greek for Koine Greek
    return 'en';
  };

  // Strip Greek diacritics for TTS (keeps base letters, removes accents/breathing marks)
  // This fixes TTS pronunciation issues while keeping diacritics visible in the text
  const stripGreekDiacritics = (text) => {
    // Greek diacritical marks Unicode ranges:
    // U+0300-U+036F: Combining Diacritical Marks
    // U+1F00-U+1FFF: Greek Extended (polytonic Greek with breathing marks, accents, iota subscript)
    
    // Normalize to NFD (decomposed form) then remove combining marks
    let normalized = text.normalize('NFD');
    
    // Remove combining diacritical marks (accents, breathing marks, etc.)
    normalized = normalized.replace(/[\u0300-\u036f]/g, '');
    
    // Handle precomposed polytonic Greek characters (U+1F00-U+1FFF)
    // Map them to their base letters
    const polytonicMap = {
      // Alpha variants
      'ἀ': 'α', 'ἁ': 'α', 'ἂ': 'α', 'ἃ': 'α', 'ἄ': 'α', 'ἅ': 'α', 'ἆ': 'α', 'ἇ': 'α',
      'Ἀ': 'Α', 'Ἁ': 'Α', 'Ἂ': 'Α', 'Ἃ': 'Α', 'Ἄ': 'Α', 'Ἅ': 'Α', 'Ἆ': 'Α', 'Ἇ': 'Α',
      'ὰ': 'α', 'ά': 'α', 'ᾀ': 'α', 'ᾁ': 'α', 'ᾂ': 'α', 'ᾃ': 'α', 'ᾄ': 'α', 'ᾅ': 'α',
      'ᾆ': 'α', 'ᾇ': 'α', 'ᾰ': 'α', 'ᾱ': 'α', 'ᾲ': 'α', 'ᾳ': 'α', 'ᾴ': 'α', 'ᾶ': 'α', 'ᾷ': 'α',
      // Epsilon variants
      'ἐ': 'ε', 'ἑ': 'ε', 'ἒ': 'ε', 'ἓ': 'ε', 'ἔ': 'ε', 'ἕ': 'ε',
      'Ἐ': 'Ε', 'Ἑ': 'Ε', 'Ἒ': 'Ε', 'Ἓ': 'Ε', 'Ἔ': 'Ε', 'Ἕ': 'Ε',
      'ὲ': 'ε', 'έ': 'ε',
      // Eta variants
      'ἠ': 'η', 'ἡ': 'η', 'ἢ': 'η', 'ἣ': 'η', 'ἤ': 'η', 'ἥ': 'η', 'ἦ': 'η', 'ἧ': 'η',
      'Ἠ': 'Η', 'Ἡ': 'Η', 'Ἢ': 'Η', 'Ἣ': 'Η', 'Ἤ': 'Η', 'Ἥ': 'Η', 'Ἦ': 'Η', 'Ἧ': 'Η',
      'ὴ': 'η', 'ή': 'η', 'ᾐ': 'η', 'ᾑ': 'η', 'ᾒ': 'η', 'ᾓ': 'η', 'ᾔ': 'η', 'ᾕ': 'η',
      'ᾖ': 'η', 'ᾗ': 'η', 'ῂ': 'η', 'ῃ': 'η', 'ῄ': 'η', 'ῆ': 'η', 'ῇ': 'η',
      // Iota variants
      'ἰ': 'ι', 'ἱ': 'ι', 'ἲ': 'ι', 'ἳ': 'ι', 'ἴ': 'ι', 'ἵ': 'ι', 'ἶ': 'ι', 'ἷ': 'ι',
      'Ἰ': 'Ι', 'Ἱ': 'Ι', 'Ἲ': 'Ι', 'Ἳ': 'Ι', 'Ἴ': 'Ι', 'Ἵ': 'Ι', 'Ἶ': 'Ι', 'Ἷ': 'Ι',
      'ὶ': 'ι', 'ί': 'ι', 'ῐ': 'ι', 'ῑ': 'ι', 'ῒ': 'ι', 'ΐ': 'ι', 'ῖ': 'ι', 'ῗ': 'ι',
      // Omicron variants
      'ὀ': 'ο', 'ὁ': 'ο', 'ὂ': 'ο', 'ὃ': 'ο', 'ὄ': 'ο', 'ὅ': 'ο',
      'Ὀ': 'Ο', 'Ὁ': 'Ο', 'Ὂ': 'Ο', 'Ὃ': 'Ο', 'Ὄ': 'Ο', 'Ὅ': 'Ο',
      'ὸ': 'ο', 'ό': 'ο',
      // Upsilon variants
      'ὐ': 'υ', 'ὑ': 'υ', 'ὒ': 'υ', 'ὓ': 'υ', 'ὔ': 'υ', 'ὕ': 'υ', 'ὖ': 'υ', 'ὗ': 'υ',
      'Ὑ': 'Υ', 'Ὓ': 'Υ', 'Ὕ': 'Υ', 'Ὗ': 'Υ',
      'ὺ': 'υ', 'ύ': 'υ', 'ῠ': 'υ', 'ῡ': 'υ', 'ῢ': 'υ', 'ΰ': 'υ', 'ῦ': 'υ', 'ῧ': 'υ',
      // Omega variants
      'ὠ': 'ω', 'ὡ': 'ω', 'ὢ': 'ω', 'ὣ': 'ω', 'ὤ': 'ω', 'ὥ': 'ω', 'ὦ': 'ω', 'ὧ': 'ω',
      'Ὠ': 'Ω', 'Ὡ': 'Ω', 'Ὢ': 'Ω', 'Ὣ': 'Ω', 'Ὤ': 'Ω', 'Ὥ': 'Ω', 'Ὦ': 'Ω', 'Ὧ': 'Ω',
      'ὼ': 'ω', 'ώ': 'ω', 'ᾠ': 'ω', 'ᾡ': 'ω', 'ᾢ': 'ω', 'ᾣ': 'ω', 'ᾤ': 'ω', 'ᾥ': 'ω',
      'ᾦ': 'ω', 'ᾧ': 'ω', 'ῲ': 'ω', 'ῳ': 'ω', 'ῴ': 'ω', 'ῶ': 'ω', 'ῷ': 'ω',
      // Rho variants
      'ῤ': 'ρ', 'ῥ': 'ρ', 'Ῥ': 'Ρ',
    };
    
    // Replace polytonic characters with base letters
    let result = '';
    for (const char of normalized) {
      result += polytonicMap[char] || char;
    }
    
    return result;
  };

  // Play audio for current verse in sentence mode
  const playVerseAudio = async () => {
    if (!currentVerse || isPlayingAudio) {
      // Stop playback if already playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
        setIsPlayingAudio(false);
      }
      return;
    }

    setIsPlayingAudio(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      let verseText = currentVerse.tokens
        .filter(t => t.is_word)
        .map(t => t.surface_form)
        .join(' ');
      
      const langCode = getTTSLanguageCode();
      
      // Strip diacritics for Greek text to improve TTS pronunciation
      if (courseId === 'greek_nt' || courseId === 'biblical_greek') {
        verseText = stripGreekDiacritics(verseText);
      }
      
      const response = await fetch(`${BACKEND_URL}/api/tts/synthesize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          text: verseText,
          language: langCode,
          speed: 0.85 // Slightly slower for learning
        })
      });

      if (!response.ok) {
        throw new Error('TTS request failed');
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);

      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => {
        setIsPlayingAudio(false);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };

      audio.onerror = () => {
        setIsPlayingAudio(false);
        console.error('Audio playback error');
      };

      await audio.play();
    } catch (err) {
      console.error('TTS error:', err);
      setIsPlayingAudio(false);
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Calculate words that will be auto-marked as known
  const getCompletionStats = useCallback(() => {
    if (!chapterData?.verses) return { total: 0, clicked: 0, unclicked: 0 };
    
    const allWords = new Set();
    chapterData.verses.forEach(verse => {
      verse.tokens.forEach(token => {
        if (token.is_word && token.normalized_form) {
          allWords.add(token.normalized_form);
        }
      });
    });
    
    const unclickedWords = [...allWords].filter(w => !clickedWords.has(w));
    
    return {
      total: allWords.size,
      clicked: clickedWords.size,
      unclicked: unclickedWords.length
    };
  }, [chapterData, clickedWords]);

  const handleCompleteClick = () => {
    setShowCompleteConfirm(true);
  };

  const completeLesson = async () => {
    setShowCompleteConfirm(false);
    try {
      setCompletingLesson(true);
      await api.post('/bible/progress/complete-lesson', {
        course_id: courseId,
        book_id: bookId,
        chapter: chapterNum,
        clicked_words: Array.from(clickedWords)
      });
      
      // Refresh to show completion
      await fetchChapterContent();
      
      // Navigate to next chapter if available
      if (chapterData?.next_chapter) {
        navigate(`/bible/${courseId}/${chapterData.next_chapter.book_id}/${chapterData.next_chapter.chapter}`);
      }
    } catch (err) {
      console.error('Failed to complete lesson:', err);
    } finally {
      setCompletingLesson(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (error || !chapterData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error || 'Chapter not found'}</p>
          <Link to="/bible-courses" className="text-amber-400 hover:text-amber-300">
            Back to Bible Courses
          </Link>
        </div>
      </div>
    );
  }

  const { book, verses, language, progress, next_chapter, prev_chapter } = chapterData;
  const isRTL = language?.direction === 'rtl' || courseId === 'biblical_hebrew';
  const currentVerse = mode === 'sentence' ? verses[currentSentenceIndex] : null;

  return (
    <div 
      ref={containerRef}
      className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-6 px-4"
      onClick={() => popover.close()}
    >
      <div className="max-w-4xl mx-auto">
        {/* Navigation Header */}
        <div className="flex items-center justify-between mb-6">
          <Link 
            to={`/bible/${courseId}/${bookId}`}
            className="flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>{book?.name}</span>
          </Link>
          
          <div className="flex items-center gap-4">
            {/* Mode Toggle */}
            <div className="flex items-center bg-slate-800 rounded-lg p-1">
              <button
                data-testid="mode-text"
                onClick={() => setMode('text')}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm transition-colors
                  ${mode === 'text' 
                    ? 'bg-amber-500 text-slate-900' 
                    : 'text-slate-400 hover:text-white'}`}
              >
                <AlignLeft className="w-4 h-4" />
                <span>{t('bibleReader.modeText')}</span>
              </button>
              <button
                data-testid="mode-sentence"
                onClick={() => setMode('sentence')}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm transition-colors
                  ${mode === 'sentence' 
                    ? 'bg-amber-500 text-slate-900' 
                    : 'text-slate-400 hover:text-white'}`}
              >
                <Type className="w-4 h-4" />
                <span>Sentence</span>
              </button>
            </div>
          </div>
        </div>

        {/* Chapter Title */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white mb-1">
            {book?.name} {chapterNum}
          </h1>
          {progress?.completed && (
            <span className="inline-flex items-center gap-1 text-green-400 text-sm">
              <CheckCircle className="w-4 h-4" />
              {t('bibleReader.completed')}
            </span>
          )}
        </div>

        {/* Reading Content */}
        <div ref={contentRef} className={`bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 p-6 md:p-8 mb-6`}>
          {mode === 'text' ? (
            // Text Mode - Paginated verses
            <div>
              {/* Page indicator */}
              {totalPages > 1 && (
                <div className={`flex items-center justify-between mb-4 pb-4 border-b border-slate-700 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span className="text-sm text-slate-400">
                    Verses {currentPage * versesPerPage + 1}-{Math.min((currentPage + 1) * versesPerPage, verses.length)} of {verses.length}
                  </span>
                  <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <button
                      data-testid="prev-page"
                      onClick={() => navigatePage('prev')}
                      disabled={currentPage === 0}
                      className="p-1.5 rounded bg-slate-700 text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600 transition-colors"
                    >
                      {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                    </button>
                    <span className="text-sm text-slate-400 min-w-[60px] text-center">
                      {currentPage + 1} / {totalPages}
                    </span>
                    <button
                      data-testid="next-page"
                      onClick={() => navigatePage('next')}
                      disabled={currentPage >= totalPages - 1}
                      className="p-1.5 rounded bg-slate-700 text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600 transition-colors"
                    >
                      {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}
              
              <div 
                className={`space-y-4 ${isRTL ? 'text-right font-hebrew' : ''}`}
                dir={isRTL ? 'rtl' : 'ltr'}
              >
                {paginatedVerses.map((verse) => (
                  <div key={verse.verse_id} className="leading-relaxed">
                    <span className="text-amber-500 font-semibold mr-2 text-sm">
                      {verse.verse}
                    </span>
                    {verse.tokens.map((token, idx) => {
                      // Use backend-computed color based on word status/strength (like Reader.js)
                      const tokenColor = token.color || '#EF4444'; // Default to red for new words
                      const isKnownOrAbove = (token.strength || 0) >= 0.8 || token.status === 'known' || token.status === 'mastered';
                      const isIgnored = token.status === 'ignored';
                      
                      return (
                        <span
                          key={token.token_id || idx}
                          data-testid={`word-${verse.verse}-${idx}`}
                          onClick={(e) => token.is_word && handleWordClick(token, e)}
                          className={`
                            ${token.is_word 
                              ? 'cursor-pointer hover:bg-amber-500/20 rounded px-0.5 transition-colors'
                              : 'text-slate-500'
                            }
                            text-xl md:text-2xl
                          `}
                          style={token.is_word ? { 
                            color: (isKnownOrAbove || isIgnored) ? 'inherit' : tokenColor 
                          } : undefined}
                        >
                          {token.surface_form}
                          {token.is_word && idx < verse.tokens.length - 1 ? ' ' : ''}
                        </span>
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* Bottom page navigation */}
              {totalPages > 1 && (
                <div className={`flex items-center justify-center gap-4 mt-6 pt-4 border-t border-slate-700 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <button
                    onClick={() => navigatePage('prev')}
                    disabled={currentPage === 0}
                    className={`flex items-center gap-1 px-4 py-2 rounded-lg bg-slate-700 text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                  >
                    {isRTL ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
                    <span>{t('bibleReader.previousPage')}</span>
                  </button>
                  <span className="text-slate-400">
                    {t('bibleReader.pageOf', { current: currentPage + 1, total: totalPages })}
                  </span>
                  <button
                    onClick={() => navigatePage('next')}
                    disabled={currentPage >= totalPages - 1}
                    className={`flex items-center gap-1 px-4 py-2 rounded-lg bg-slate-700 text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                  >
                    <span>{t('bibleReader.nextPage')}</span>
                    {isRTL ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                  </button>
                </div>
              )}
            </div>
          ) : (
            // Sentence Mode - One verse at a time with adjustable font and grammar sidebar
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Main verse display */}
              <div className="flex-1 min-h-[300px] flex flex-col items-center justify-center">
                {currentVerse && (
                  <>
                    {/* Verse indicator and font size controls */}
                    <div className="flex items-center justify-between w-full mb-4">
                      <div className="text-amber-500 text-sm">
                        {t('bibleReader.verseOf', { current: currentVerse.verse, total: verses.length })}
                      </div>
                      
                      {/* Audio and Font Size Controls */}
                      <div className="flex items-center gap-3">
                        {/* Audio Play Button */}
                        <button
                          data-testid="play-verse-audio"
                          onClick={playVerseAudio}
                          disabled={generatingAudio}
                          className={`p-2 rounded-lg transition-colors ${
                            isPlayingAudio 
                              ? 'bg-amber-500 text-slate-900' 
                              : 'bg-slate-700/50 text-slate-300 hover:bg-slate-600'
                          }`}
                          title={isPlayingAudio ? t('bibleReader.stopAudio') : t('bibleReader.playVerseAudio')}
                        >
                          <Volume2 className={`w-4 h-4 ${isPlayingAudio ? 'animate-pulse' : ''}`} />
                        </button>
                        
                        {/* Font Size Controls */}
                        <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-2 py-1">
                        <button
                          data-testid="decrease-font"
                          onClick={decreaseFontSize}
                          disabled={sentenceFontSize <= 16}
                          className="p-1 rounded hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Decrease font size"
                        >
                          <span className="text-xs font-bold text-slate-400">A-</span>
                        </button>
                        <span className="text-xs text-slate-400 min-w-[40px] text-center">
                          {sentenceFontSize}px
                        </span>
                        <button
                          data-testid="increase-font"
                          onClick={increaseFontSize}
                          disabled={sentenceFontSize >= 32}
                          className="p-1 rounded hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title={t('bibleReader.increaseFont')}
                        >
                          <span className="text-sm font-bold text-slate-400">A+</span>
                        </button>
                        </div>
                      </div>
                    </div>
                    
                    <div 
                      className={`text-center leading-loose ${isRTL ? 'font-hebrew' : ''}`}
                      dir={isRTL ? 'rtl' : 'ltr'}
                    >
                      {currentVerse.tokens.map((token, idx) => {
                        // Use backend-computed color based on word status/strength (like Reader.js)
                        const tokenColor = token.color || '#EF4444'; // Default to red for new words
                        const isKnownOrAbove = (token.strength || 0) >= 0.8 || token.status === 'known' || token.status === 'mastered';
                        const isIgnored = token.status === 'ignored';
                        
                        return (
                          <span
                            key={token.token_id || idx}
                            data-testid={`sentence-word-${idx}`}
                            onClick={(e) => token.is_word && handleWordClick(token, e)}
                            className={`
                              ${token.is_word 
                                ? 'cursor-pointer hover:bg-amber-500/20 rounded px-1 transition-colors'
                                : 'text-slate-500'
                              }
                            `}
                            style={{
                              fontSize: `${sentenceFontSize}px`,
                              lineHeight: 1.5,
                              ...(token.is_word ? { color: (isKnownOrAbove || isIgnored) ? 'inherit' : tokenColor } : {})
                            }}
                          >
                            {token.surface_form}
                            {token.is_word && idx < currentVerse.tokens.length - 1 ? ' ' : ''}
                          </span>
                        );
                      })}
                    </div>
                    
                    {/* Sentence Navigation */}
                    <div className={`flex items-center gap-4 mt-8 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <button
                        data-testid="prev-sentence"
                        onClick={() => navigateSentence('prev')}
                        disabled={currentSentenceIndex === 0}
                        className="p-3 rounded-full bg-slate-700 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600 transition-colors"
                      >
                        {isRTL ? <ArrowRight className="w-5 h-5" /> : <ArrowLeft className="w-5 h-5" />}
                      </button>
                      <span className="text-slate-400">
                        {currentSentenceIndex + 1} / {verses.length}
                      </span>
                      <button
                        data-testid="next-sentence"
                        onClick={() => navigateSentence('next')}
                        disabled={currentSentenceIndex === verses.length - 1}
                        className="p-3 rounded-full bg-slate-700 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600 transition-colors"
                      >
                        {isRTL ? <ArrowLeft className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Grammar Widget Sidebar */}
              <div className="lg:w-72 shrink-0">
                <GrammarWidget
                  grammarPoints={currentVerse?.grammar_points || verseGrammarPoints}
                  onSelectGrammar={setSelectedGrammar}
                  language={courseId === 'biblical_hebrew' ? 'hebrew' : 'greek'}
                  isLoading={grammarLoading}
                />
              </div>
            </div>
          )}
        </div>

        {/* Chapter Navigation and Complete */}
        <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
          <button
            data-testid="prev-chapter"
            onClick={() => navigateChapter('prev')}
            disabled={!prev_chapter}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-slate-300 
              disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-700 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            {isRTL ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            <span>Previous</span>
          </button>

          {/* Show Mark Complete only on last page in Text mode OR when on last sentence in Sentence mode */}
          {((mode === 'text' && isLastPage) || (mode === 'sentence' && currentSentenceIndex === verses.length - 1)) ? (
            <button
              data-testid="complete-lesson"
              onClick={handleCompleteClick}
              disabled={completingLesson || progress?.completed}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold transition-colors
                ${progress?.completed 
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                  : 'bg-amber-500 hover:bg-amber-400 text-slate-900'}`}
            >
              {completingLesson ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-900 border-t-transparent"></div>
                  <span>Completing...</span>
                </>
              ) : progress?.completed ? (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span>Completed</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span>Mark Complete</span>
                </>
              )}
            </button>
          ) : (
            <div className="text-sm text-slate-500">
              {mode === 'text' 
                ? `${totalPages - currentPage - 1} pages remaining`
                : `${verses.length - currentSentenceIndex - 1} verses remaining`
              }
            </div>
          )}

          <button
            data-testid="next-chapter"
            onClick={() => navigateChapter('next')}
            disabled={!next_chapter}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-slate-300 
              disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-700 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <span>{t('bibleReader.next')}</span>
            {isRTL ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </button>
        </div>

        {/* Study Stats */}
        <div className="mt-8 bg-slate-800/30 rounded-lg p-4 border border-slate-700">
          <h3 className="text-sm font-semibold text-slate-400 mb-3">Study Progress</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-white">{verses.length}</p>
              <p className="text-xs text-slate-500">Verses</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-400">{clickedWords.size}</p>
              <p className="text-xs text-slate-500">Words Studied</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-400">
                {verses.reduce((sum, v) => sum + v.word_count, 0)}
              </p>
              <p className="text-xs text-slate-500">Total Words</p>
            </div>
          </div>
        </div>
      </div>

      {/* Word Popover — shared popover (#6/#56 unification) */}
      {popover.selectedToken && popover.popoverPos && (
        <SharedWordPopover
          style={popover.popoverPos}
          wordData={popover.wordData}
          wordLoading={popover.wordLoading}
          language={{ code: bibleLangCode }}
          token={popover.selectedToken}
          onClose={() => popover.close()}
          onSetStatus={(newStatus) => {
            const wordToUpdate = popover.selectedToken?.normalized_form || popover.selectedToken?.surface_form;
            const statusColors = {
              'ignored': 'inherit', 'new': '#EF4444', 'learning': '#F97316',
              'review': '#EAB308', 'familiar': '#22C55E', 'known': 'inherit', 'mastered': 'inherit'
            };
            const strengthMap = {
              'ignored': null, 'new': 0.0, 'learning': 0.2, 'review': 0.3,
              'familiar': 0.5, 'known': 0.8, 'mastered': 1.0
            };
            setChapterData(prev => {
              if (!prev?.verses) return prev;
              return {
                ...prev,
                verses: prev.verses.map(verse => ({
                  ...verse,
                  tokens: verse.tokens.map(token => {
                    if (token.normalized_form === wordToUpdate || token.surface_form === wordToUpdate) {
                      return { ...token, status: newStatus, strength: strengthMap[newStatus] ?? 0, color: statusColors[newStatus] || token.color };
                    }
                    return token;
                  })
                }))
              };
            });
            popover.close();
          }}
          locked={popover.wordData?.locked}
          lockedFields={popover.wordData?.locked_fields || []}
          onRelatedWordClick={(term) => popover.openForTerm(term)}
          isNewEntry={popover.isNewEntry}
        />
      )}

      {/* Completion Confirmation Dialog */}
      <AlertDialog open={showCompleteConfirm} onOpenChange={setShowCompleteConfirm}>
        <AlertDialogContent className="bg-slate-800 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-white">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              {t('bibleReader.completeLesson')}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-slate-300 space-y-3">
                <p>
                  {t('bibleReader.aboutToComplete', { book: book?.name, chapter: chapterNum })}
                </p>
                
                <div className="bg-slate-900/50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span>{t('bibleReader.totalUniqueWords')}</span>
                    <span className="text-white font-medium">{getCompletionStats().total}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('bibleReader.wordsClicked')}</span>
                    <span className="text-amber-400 font-medium">{getCompletionStats().clicked}</span>
                  </div>
                  <div className="flex justify-between border-t border-slate-700 pt-2 mt-2">
                    <span>{t('bibleReader.wordsToBeKnown')}</span>
                    <span className="text-green-400 font-medium">{getCompletionStats().unclicked}</span>
                  </div>
                </div>
                
                <p className="text-amber-400 text-sm">
                  {t('bibleReader.autoMarkWarning', { count: getCompletionStats().unclicked })}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 text-white border-slate-600 hover:bg-slate-600">
              {t('bibleReader.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={completeLesson}
              className="bg-amber-500 text-slate-900 hover:bg-amber-400"
            >
              {t('bibleReader.yesComplete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Grammar Overlay */}
      {selectedGrammar && (
        <GrammarOverlay
          topic={selectedGrammar.topic_detail || selectedGrammar}
          verseContext={currentVerse?.text}
          onClose={() => setSelectedGrammar(null)}
          language={courseId === 'biblical_hebrew' ? 'hebrew' : 'greek'}
        />
      )}
    </div>
  );
};

export default BibleReader;
